import React, { useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
// calculatePlayerStats ve getAllTimeStats artık kullanılmıyor - inline hesaplama yapıyoruz
import { Player, SeasonStats, Season, Match, shouldApplyParticipationBonus, FIRST_SEASON_ID } from '../types';
import PlayerProfileModal from '../components/PlayerProfileModal';
import { toTurkishUpperCase } from '../utils/turkishUtils';
import { ensureHttps } from '../utils/urlUtils';
import { convertPlayerToGuestInMatches } from '../utils/matchStats';
import './StandingsPage.css';

const StandingsPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [playersStats, setPlayersStats] = useState<{ [playerId: string]: SeasonStats }>({});
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<string>('default');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [seasonMatches, setSeasonMatches] = useState<Match[]>([]);
  const [rankChanges, setRankChanges] = useState<{ [playerId: string]: 'up' | 'down' | 'same' | null }>({});
  const [showAddPlayerForm, setShowAddPlayerForm] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerPosition, setNewPlayerPosition] = useState('');
  const [newPlayerCode, setNewPlayerCode] = useState('');
  const [newPlayerImage, setNewPlayerImage] = useState<string>('');
  const isAdmin = storage.isAdminMode();

  // Oyuncunun sıralama değişimini hesapla (yükseliş/düşüş için) - OPTİMİZE EDİLDİ
  // Artık tüm oyuncular için tek seferde hesaplanıyor, her oyuncu için ayrı API çağrısı yok
  const calculateRankChanges = useCallback(async (
    allPlayers: Player[], 
    allMatches: Match[], 
    currentRankings: { playerId: string; rank: number }[]
  ): Promise<{ [playerId: string]: 'up' | 'down' | 'same' | null }> => {
    const filteredMatches = selectedSeason === 'all' 
      ? allMatches 
      : allMatches.filter(m => m.seasonId === selectedSeason);
    
    if (filteredMatches.length === 0) return {};
    
    // En son maçı bul
    const sortedMatches = [...filteredMatches].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const lastMatch = sortedMatches[0];
    
    // Son maçtan önceki sıralamayı hesapla
    const matchesBeforeLast = sortedMatches.filter(m => 
      new Date(m.date).getTime() < new Date(lastMatch.date).getTime()
    );
    
    if (matchesBeforeLast.length === 0) return {};
    
    // Son maçtan önceki istatistikleri hesapla (tüm oyuncular için tek seferde)
    const playersWithStatsBefore = allPlayers.map(player => {
      let stats: SeasonStats = {
        matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
        ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
      };
      let winsS1 = 0, lossesS1 = 0, winsS2Plus = 0, drawsS2Plus = 0;

      matchesBeforeLast.forEach((match: Match) => {
        if (!match || !match.blackTeam || !match.whiteTeam) return;

        const isInBlackTeam = match.blackTeam.includes(player.id);
        const isInWhiteTeam = match.whiteTeam.includes(player.id);
        const played = isInBlackTeam || isInWhiteTeam;

        if (played) {
          stats.matches += 1;
          const blackWon = match.blackScore > match.whiteScore;
          const whiteWon = match.whiteScore > match.blackScore;
          const draw = match.blackScore === match.whiteScore;

          const isWin = (isInBlackTeam && blackWon) || (isInWhiteTeam && whiteWon);
          const isLoss = (isInBlackTeam && !blackWon && !draw) || (isInWhiteTeam && !whiteWon && !draw);
          if (match.seasonId === FIRST_SEASON_ID) {
            if (isWin) winsS1 += 1;
            else if (isLoss) lossesS1 += 1;
          } else {
            if (isWin) winsS2Plus += 1;
            else if (draw) drawsS2Plus += 1;
          }

          if (isInBlackTeam) {
            if (blackWon) stats.wins += 1;
            else if (draw) stats.draws += 1;
            else stats.losses += 1;
          } else {
            if (whiteWon) stats.wins += 1;
            else if (draw) stats.draws += 1;
            else stats.losses += 1;
          }

          if (match.events && Array.isArray(match.events)) {
            match.events.forEach((event: any) => {
              if (event.scorerId === player.id && !event.ownGoal) stats.goals += 1;
              if (event.scorerId === player.id && event.ownGoal) stats.ownGoals += 1;
              if (event.assistId === player.id) stats.assists += 1;
            });
          }

          if (match.blackCard === player.id) stats.blackCards += 1;
          if (match.mvdp === player.id) stats.mvdpCards += 1;
        }
      });

      // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
      if (selectedSeason === 'all') {
        stats.points = (winsS1 - lossesS1) + (winsS2Plus * 3) + (drawsS2Plus * 1);
      } else if (selectedSeason === FIRST_SEASON_ID) {
        stats.points = stats.wins - stats.losses;
      } else {
        stats.points = stats.wins * 3 + stats.draws * 1;
      }
      return { player, stats };
    });
    
    // Önceki sıralamayı hesapla (1. sezon: eski sıralama, 2. sezon+: yeni sıralama)
    const useNewStandingsSort = selectedSeason !== FIRST_SEASON_ID;
    playersWithStatsBefore.sort((a, b) => {
      if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
      if (useNewStandingsSort) {
        if (b.stats.matches !== a.stats.matches) return b.stats.matches - a.stats.matches;
        if (b.stats.wins !== a.stats.wins) return b.stats.wins - a.stats.wins;
      }
      const aCards = a.stats.blackCards + a.stats.mvdpCards;
      const bCards = b.stats.blackCards + b.stats.mvdpCards;
      if (bCards !== aCards) return bCards - aCards;
      const aContribution = a.stats.goals + a.stats.assists;
      const bContribution = b.stats.goals + b.stats.assists;
      if (bContribution !== aContribution) return bContribution - aContribution;
      if (useNewStandingsSort) {
        if (b.stats.goals !== a.stats.goals) return b.stats.goals - a.stats.goals;
        return b.stats.assists - a.stats.assists;
      }
      return b.stats.matches - a.stats.matches;
    });
    
    // Rank değişikliklerini hesapla
    const rankChanges: { [playerId: string]: 'up' | 'down' | 'same' | null } = {};
    currentRankings.forEach(({ playerId, rank }) => {
      const previousRank = playersWithStatsBefore.findIndex(p => p.player.id === playerId) + 1;
      if (previousRank === 0) {
        rankChanges[playerId] = 'same';
      } else if (rank < previousRank) {
        rankChanges[playerId] = 'up';
      } else if (rank > previousRank) {
        rankChanges[playerId] = 'down';
      } else {
        rankChanges[playerId] = 'same';
      }
    });
    
    return rankChanges;
  }, [selectedSeason]);

  const loadPlayers = useCallback(async () => {
    try {
      // Oyuncuları API'den yükle (cache otomatik yönetiliyor)
      const allPlayers = await storage.getPlayers();
      
      if (!allPlayers || allPlayers.length === 0) {
        console.warn('[StandingsPage] Oyuncu bulunamadı');
        setPlayers([]);
        setPlayersStats({});
        setSeasonMatches([]);
        return;
      }
      
      // Oyuncuları hemen göster (UI hızlı gösterilsin)
      setPlayers(allPlayers);
      
      // Maçları bir kez çek (API'den - cache otomatik yönetiliyor)
      const allMatches = await storage.getMatches();
      
      if (!allMatches || !Array.isArray(allMatches)) {
        console.warn('[StandingsPage] Maç bulunamadı veya geçersiz format');
        // Oyuncuları stats olmadan göster
        setPlayers(allPlayers);
        setPlayersStats({});
        setSeasonMatches([]);
        return;
      }
      
      // Sezon maçlarını filtrele
      const seasonMatches = selectedSeason === 'all' 
        ? allMatches 
        : allMatches.filter((m: Match) => m && m.seasonId === selectedSeason);
      
      // İstatistikleri tek seferde hesapla (tüm oyuncular için - mobil optimizasyonu kaldırıldı)
      const playersWithStatsPromises = allPlayers.map(async (player: Player) => {
      // İstatistikleri cache'lenmiş maçlarla hesapla
      let stats: SeasonStats = {
        matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
        ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
      };
      let winsS1 = 0, lossesS1 = 0, winsS2Plus = 0, drawsS2Plus = 0;

      seasonMatches.forEach((match: Match) => {
        if (!match || !match.blackTeam || !match.whiteTeam) return;

        const isInBlackTeam = match.blackTeam.includes(player.id);
        const isInWhiteTeam = match.whiteTeam.includes(player.id);
        const played = isInBlackTeam || isInWhiteTeam;

        if (played) {
          stats.matches += 1;
          const blackWon = match.blackScore > match.whiteScore;
          const whiteWon = match.whiteScore > match.blackScore;
          const draw = match.blackScore === match.whiteScore;

          const isWin = (isInBlackTeam && blackWon) || (isInWhiteTeam && whiteWon);
          const isLoss = (isInBlackTeam && !blackWon && !draw) || (isInWhiteTeam && !whiteWon && !draw);
          if (match.seasonId === FIRST_SEASON_ID) {
            if (isWin) winsS1 += 1;
            else if (isLoss) lossesS1 += 1;
          } else {
            if (isWin) winsS2Plus += 1;
            else if (draw) drawsS2Plus += 1;
          }

          if (isInBlackTeam) {
            if (blackWon) stats.wins += 1;
            else if (draw) stats.draws += 1;
            else stats.losses += 1;
          } else {
            if (whiteWon) stats.wins += 1;
            else if (draw) stats.draws += 1;
            else stats.losses += 1;
          }

          if (match.events && Array.isArray(match.events)) {
            match.events.forEach((event: any) => {
              if (event.scorerId === player.id && !event.ownGoal) stats.goals += 1;
              if (event.scorerId === player.id && event.ownGoal) stats.ownGoals += 1;
              if (event.assistId === player.id) stats.assists += 1;
            });
          }

          if (match.blackCard === player.id) stats.blackCards += 1;
          if (match.mvdp === player.id) stats.mvdpCards += 1;
        }
      });

        // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
        if (selectedSeason === 'all') {
          stats.points = (winsS1 - lossesS1) + (winsS2Plus * 3) + (drawsS2Plus * 1);
        } else if (selectedSeason === FIRST_SEASON_ID) {
          stats.points = stats.wins - stats.losses;
        } else {
          stats.points = stats.wins * 3 + stats.draws * 1;
        }
        return { player, stats };
      });
      
      const playersWithStats = await Promise.all(playersWithStatsPromises);

      // 1. sezon: eski sıralama, 2. sezon+: yeni sıralama (puan → maç → galibiyet → kart → gol katkısı → gol → asist)
      const useNewStandingsSort = selectedSeason !== FIRST_SEASON_ID;
      playersWithStats.sort((a, b) => {
        if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
        if (useNewStandingsSort) {
          if (b.stats.matches !== a.stats.matches) return b.stats.matches - a.stats.matches;
          if (b.stats.wins !== a.stats.wins) return b.stats.wins - a.stats.wins;
        }
        const aCards = a.stats.blackCards + a.stats.mvdpCards;
        const bCards = b.stats.blackCards + b.stats.mvdpCards;
        if (bCards !== aCards) return bCards - aCards;
        const aContribution = a.stats.goals + a.stats.assists;
        const bContribution = b.stats.goals + b.stats.assists;
        if (bContribution !== aContribution) return bContribution - aContribution;
        if (useNewStandingsSort) {
          if (b.stats.goals !== a.stats.goals) return b.stats.goals - a.stats.goals;
          return b.stats.assists - a.stats.assists;
        }
        return b.stats.matches - a.stats.matches;
      });

      const sortedPlayers = playersWithStats.map(p => p.player);
      setPlayers(sortedPlayers);
      setSeasonMatches(seasonMatches);
      
      // Stats'leri state'e kaydet
      const statsMap: { [playerId: string]: SeasonStats } = {};
      playersWithStats.forEach(({ player, stats }) => {
        statsMap[player.id] = stats;
      });
      setPlayersStats(statsMap);
      
      // Rank değişikliklerini arka planda hesapla (optimize edildi - tek seferde tüm oyuncular için)
      // Sadece ilk 50 oyuncu için rank değişikliği hesapla (performans için)
      setTimeout(async () => {
        try {
          const currentRankings = sortedPlayers.slice(0, 50).map((player, index) => ({
            playerId: player.id,
            rank: index + 1
          }));
          const rankChangesResult = await calculateRankChanges(allPlayers, allMatches, currentRankings);
          setRankChanges(rankChangesResult);
        } catch (err) {
          console.warn('[StandingsPage] Rank change hesaplama hatası:', err);
        }
      }, 500);
    } catch (error) {
      console.error('[StandingsPage] loadPlayers hatası:', error);
      // Hata durumunda en azından oyuncuları göster (stats olmadan)
      try {
        const allPlayers = await storage.getPlayers();
        if (allPlayers && allPlayers.length > 0) {
          setPlayers(allPlayers);
          setPlayersStats({});
        }
      } catch (fallbackError) {
        console.error('[StandingsPage] Fallback yükleme hatası:', fallbackError);
      }
    }
  }, [selectedSeason, calculateRankChanges]);

  // İlk yükleme - sadece bir kez çalış (cache-first stratejisi)
  useEffect(() => {
    const initialize = async () => {
      // Sezonları yükle (API'den - cache otomatik yönetiliyor)
      const loadedSeasons = await storage.getSeasons();
      setSeasons(loadedSeasons);
      
      const players = await storage.getPlayers();
      setPlayers(players);
      
      // Mevcut sezonu ayarla
      const currentSeasonId = await storage.getCurrentSeason();
      if (currentSeasonId) {
        setSelectedSeason(currentSeasonId);
      } else {
        setSelectedSeason('all');
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sadece ilk render'da çalış

  // selectedSeason veya seasons değiştiğinde loadPlayers'ı çağır
  useEffect(() => {
    if (selectedSeason && seasons.length > 0) {
      console.log('StandingsPage: selectedSeason or seasons changed, loading players...');
      loadPlayers();
    } else if (selectedSeason === 'all' && seasons.length > 0) {
      // 'all' seçildiğinde de yükle
      console.log('StandingsPage: loading all players...');
      loadPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, seasons.length]); // loadPlayers'ı dependency'den çıkar


  // Son maçtan önceki istatistikleri hesaplamak için yardımcı fonksiyon
  const calculatePlayerStatsBefore = async (playerId: string, seasonId: string, matchesBefore: any[]): Promise<SeasonStats> => {
    const allPlayers = await storage.getPlayers();
    const player = allPlayers.find(p => p.id === playerId);
    if (!player) {
      return {
        matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
        ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
      };
    }

    let stats: SeasonStats = {
      matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
      ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
    };
    let winsS1 = 0, lossesS1 = 0, winsS2Plus = 0, drawsS2Plus = 0;

    matchesBefore.forEach(match => {
      // Match verilerinin geçerli olduğundan emin ol
      if (!match || !match.blackTeam || !match.whiteTeam) {
        return;
      }

      const isInBlackTeam = match.blackTeam.includes(playerId);
      const isInWhiteTeam = match.whiteTeam.includes(playerId);
      const played = isInBlackTeam || isInWhiteTeam;

      if (played) {
        stats.matches += 1;
        const blackWon = match.blackScore > match.whiteScore;
        const whiteWon = match.whiteScore > match.blackScore;
        const draw = match.blackScore === match.whiteScore;

        const isWin = (isInBlackTeam && blackWon) || (isInWhiteTeam && whiteWon);
        const isLoss = (isInBlackTeam && !blackWon && !draw) || (isInWhiteTeam && !whiteWon && !draw);
        if (match.seasonId === FIRST_SEASON_ID) {
          if (isWin) winsS1 += 1;
          else if (isLoss) lossesS1 += 1;
        } else {
          if (isWin) winsS2Plus += 1;
          else if (draw) drawsS2Plus += 1;
        }

        if (isInBlackTeam) {
          if (blackWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        } else {
          if (whiteWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        }

        if (match.events && Array.isArray(match.events)) {
          match.events.forEach((event: any) => {
            if (event.scorerId === playerId && !event.ownGoal) stats.goals += 1;
            if (event.scorerId === playerId && event.ownGoal) stats.ownGoals += 1;
            if (event.assistId === playerId) stats.assists += 1;
          });
        }

        if (match.blackCard === playerId) stats.blackCards += 1;
        if (match.mvdp === playerId) stats.mvdpCards += 1;
      }
    });

    // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
    stats.points = (winsS1 - lossesS1) + (winsS2Plus * 3) + (drawsS2Plus * 1);
    return stats;
  };

  // getAllTimeStats için matches parametresi ekle
  const getAllTimeStatsBefore = async (playerId: string, matchesBefore: any[]): Promise<SeasonStats> => {
    const allPlayers = await storage.getPlayers();
    const player = allPlayers.find(p => p.id === playerId);
    if (!player) {
      return {
        matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
        ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
      };
    }

    let stats: SeasonStats = {
      matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
      ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
    };
    let winsS1 = 0, lossesS1 = 0, winsS2Plus = 0, drawsS2Plus = 0;

    matchesBefore.forEach(match => {
      // Match verilerinin geçerli olduğundan emin ol
      if (!match || !match.blackTeam || !match.whiteTeam) {
        return;
      }

      const isInBlackTeam = match.blackTeam.includes(playerId);
      const isInWhiteTeam = match.whiteTeam.includes(playerId);
      const played = isInBlackTeam || isInWhiteTeam;

      if (played) {
        stats.matches += 1;
        const blackWon = match.blackScore > match.whiteScore;
        const whiteWon = match.whiteScore > match.blackScore;
        const draw = match.blackScore === match.whiteScore;

        const isWin = (isInBlackTeam && blackWon) || (isInWhiteTeam && whiteWon);
        const isLoss = (isInBlackTeam && !blackWon && !draw) || (isInWhiteTeam && !whiteWon && !draw);
        if (match.seasonId === FIRST_SEASON_ID) {
          if (isWin) winsS1 += 1;
          else if (isLoss) lossesS1 += 1;
        } else {
          if (isWin) winsS2Plus += 1;
          else if (draw) drawsS2Plus += 1;
        }

        if (isInBlackTeam) {
          if (blackWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        } else {
          if (whiteWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        }

        if (match.events && Array.isArray(match.events)) {
          match.events.forEach((event: any) => {
            if (event.scorerId === playerId && !event.ownGoal) stats.goals += 1;
            if (event.scorerId === playerId && event.ownGoal) stats.ownGoals += 1;
            if (event.assistId === playerId) stats.assists += 1;
          });
        }

        if (match.blackCard === playerId) stats.blackCards += 1;
        if (match.mvdp === playerId) stats.mvdpCards += 1;
      }
    });

    // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
    stats.points = (winsS1 - lossesS1) + (winsS2Plus * 3) + (drawsS2Plus * 1);
    return stats;
  };

  const handleAddPlayer = async () => {
    if (!newPlayerName.trim() || !newPlayerCode.trim()) {
      alert('Lütfen oyuncu adı ve kodu girin!');
      return;
    }

    const newPlayer: Player = {
      id: `player-${Date.now()}`,
      name: newPlayerName.trim(),
      position: newPlayerPosition.trim() || 'Oyuncu',
      code: newPlayerCode.trim(),
      image: newPlayerImage || undefined,
      playerCards: [],
      stats: {},
    };

    try {
      const allPlayers = await storage.getPlayers({ forceRefresh: true });
      allPlayers.push(newPlayer);
      await storage.savePlayers(allPlayers);
    } catch (error: any) {
      alert('Oyuncu eklenemedi: ' + (error?.message || error));
      return;
    }
    
    setNewPlayerName('');
    setNewPlayerPosition('');
    setNewPlayerCode('');
    setNewPlayerImage('');
    setShowAddPlayerForm(false);
    await loadPlayers();
  };

  const handleDeletePlayer = async (playerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Bu oyuncuyu silmek istediğinize emin misiniz?')) {
      try {
        const allPlayers = await storage.getPlayers({ forceRefresh: true });
        const playerToDelete = allPlayers.find(p => p.id === playerId);
        if (playerToDelete) {
          const allMatches = await storage.getMatches({ forceRefresh: true });
          await storage.saveMatches(convertPlayerToGuestInMatches(allMatches, playerToDelete));
        }
        const updatedPlayers = allPlayers.filter(p => p.id !== playerId);
        await storage.savePlayers(updatedPlayers, { deleteIds: [playerId] });
      } catch (error: any) {
        alert('Oyuncu silinemedi: ' + (error?.message || error));
        return;
      }
      await loadPlayers();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const imageData = event.target?.result as string;
      setNewPlayerImage(imageData);
    };
    
    reader.readAsDataURL(file);
  };

  const handleProfileImageUpload = async (playerId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    // Mevcut state'ten oyuncuları al (sıralamayı korumak için)
    const currentPlayers = [...players];
    const playerIndex = currentPlayers.findIndex(p => p.id === playerId);
    
    if (playerIndex === -1) {
      console.error('Oyuncu bulunamadı:', playerId);
      return;
    }
    
    try {
      const fileSizeMB = file.size / (1024 * 1024);
      console.log('[StandingsPage] Görsel yükleniyor...', file.name, `(${fileSizeMB.toFixed(2)} MB)`);
      
      // Görseli hosting'e yükle (sıkıştırma olmadan - boyut kısıtlaması yok)
      const imageUrl = await storage.uploadImage(file, `players/${playerId}-${Date.now()}-${file.name}`);
      
      console.log('[StandingsPage] Görsel yüklendi, URL:', imageUrl);
      
      // ÖNEMLİ: URL'in geçerli olduğunu kontrol et
      if (!imageUrl || !imageUrl.startsWith('http')) {
        throw new Error('Geçersiz görsel URL\'si döndü: ' + imageUrl);
      }
      
      // Görselin gerçekten yüklendiğini doğrula (opsiyonel - performans için kaldırılabilir)
      try {
        const imgCheck = await fetch(imageUrl, { method: 'HEAD' });
        if (!imgCheck.ok) {
          console.warn('[StandingsPage] Görsel yüklendi ama erişilemiyor:', imageUrl);
        }
      } catch (err) {
        console.warn('[StandingsPage] Görsel doğrulama hatası (devam ediliyor):', err);
      }
      
      // Mevcut oyuncuları sunucudan taze al — tam liste kaydında eski cache ile silinen kişinin geri gelmesini önler
      const allPlayers = await storage.getPlayers({ forceRefresh: true });
      console.log('[StandingsPage] Mevcut oyuncu sayısı:', allPlayers.length);
      
      // Sadece ilgili oyuncuyu güncelle (TÜM diğer verileri koruyarak)
      const updatedPlayers = allPlayers.map(p => {
        if (p.id === playerId) {
          // TÜM mevcut alanları koru, sadece profileImage'ı güncelle
          const updatedPlayer = { 
            ...p,  // Tüm mevcut alanlar korunuyor
            profileImage: imageUrl  // Yeni görsel URL'i
          };
          
          // Güvenlik kontrolü: Tüm önemli alanların korunduğundan emin ol
          if (!updatedPlayer.stats) {
            updatedPlayer.stats = p.stats || {};
          }
          if (!updatedPlayer.playerCards) {
            updatedPlayer.playerCards = p.playerCards || (Array.isArray(p.playerCards) ? [] : {});
          }
          
          // ÖNEMLİ: profileImage'ın kaydedildiğini logla
          console.log('[StandingsPage] Oyuncu güncelleniyor:', {
            id: updatedPlayer.id,
            name: updatedPlayer.name,
            profileImage: updatedPlayer.profileImage,
            hasImage: !!updatedPlayer.image,
            hasPlayerCard: !!updatedPlayer.playerCard
          });
          
          return updatedPlayer;
        }
        return p; // Diğer oyuncuları olduğu gibi koru (hiçbir değişiklik yok)
      });
      
      // Önce state'i güncelle (UI hemen güncellensin, sıralama korunuyor)
      const updatedPlayer = updatedPlayers.find(p => p.id === playerId);
      if (updatedPlayer) {
        const newPlayers = [...currentPlayers];
        const playerIndex = newPlayers.findIndex(p => p.id === playerId);
        if (playerIndex !== -1) {
          newPlayers[playerIndex] = updatedPlayer;
          setPlayers(newPlayers);
        }
      }
      
      // ÖNEMLİ: Maç verilerini korumak için, kayıt öncesi kontrol et
      const matchesBefore = await storage.getMatches();
      console.log('[StandingsPage] Kayıt öncesi maç sayısı:', matchesBefore.length);
      
      // Veritabanına kaydet (sadece oyuncuları kaydet, maç verileri etkilenmez)
      // savePlayers sadece oyuncuları kaydeder, maç verilerine dokunmaz
      await storage.savePlayers(updatedPlayers);
      
      // ÖNEMLİ: Kayıt sonrası doğrulama - API'den oyuncuyu çek ve profileImage'ın kaydedildiğini kontrol et
      await new Promise(resolve => setTimeout(resolve, 2000)); // API'nin güncellenmesi için bekle (artırıldı)
      
      // Cache'i temizle ve API'den direkt çek
      const verifyPlayers = await storage.getPlayers();
      const verifiedPlayer = verifyPlayers.find(p => p.id === playerId);
      
      console.log('[StandingsPage] Doğrulama sonucu:', {
        playerId,
        expectedUrl: imageUrl,
        actualUrl: verifiedPlayer?.profileImage,
        hasProfileImage: !!verifiedPlayer?.profileImage,
        playerName: verifiedPlayer?.name
      });
      
      if (verifiedPlayer && verifiedPlayer.profileImage === imageUrl) {
        console.log('[StandingsPage] ✅ Görsel başarıyla kaydedildi ve doğrulandı:', imageUrl);
        alert('Profil fotoğrafı başarıyla yüklendi!');
      } else if (verifiedPlayer && verifiedPlayer.profileImage) {
        // Farklı bir URL kaydedilmiş olabilir (cache sorunu)
        console.warn('[StandingsPage] ⚠️ Görsel farklı URL ile kaydedilmiş:', {
          expected: imageUrl,
          actual: verifiedPlayer.profileImage
        });
        alert('Görsel yüklendi ancak farklı bir URL ile kaydedilmiş. Sayfa yenilenecek.\n\nBeklenen: ' + imageUrl + '\nKaydedilen: ' + verifiedPlayer.profileImage);
      } else {
        console.error('[StandingsPage] ❌ Görsel kaydedilemedi veya kayboldu!', {
          expected: imageUrl,
          actual: verifiedPlayer?.profileImage,
          player: verifiedPlayer
        });
        alert('UYARI: Görsel yüklendi ama doğrulanamadı. Lütfen sayfayı yenileyin ve kontrol edin.\n\nURL: ' + imageUrl);
      }
      
      // Sayfayı yenile (kullanıcı yeni fotoğrafı görebilsin)
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.reload();
      
      // Maç verilerinin korunduğunu doğrula
      const matchesAfter = await storage.getMatches();
      console.log('[StandingsPage] Kayıt sonrası maç sayısı:', matchesAfter.length);
      
      if (matchesAfter.length < matchesBefore.length) {
        console.error('[StandingsPage] UYARI: Maç verileri kayboldu!', {
          önce: matchesBefore.length,
          sonra: matchesAfter.length
        });
        alert('UYARI: Maç verileri kayboldu! Lütfen sayfayı yenileyin ve tekrar deneyin.');
        return;
      }
      
      console.log('[StandingsPage] Profil görseli başarıyla kaydedildi:', imageUrl);
      
      // Başarı mesajı
      alert(`Profil fotoğrafı başarıyla yüklendi!`);
    } catch (error: any) {
      console.error('[StandingsPage] Profil görseli yükleme hatası:', error);
      
      // Daha anlaşılır hata mesajları
      let errorMessage = error?.message || 'Bilinmeyen hata';
      if (errorMessage.includes('too large') || errorMessage.includes('413')) {
        errorMessage = 'Dosya boyutu çok büyük! Lütfen daha küçük bir görsel seçin.';
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = 'Sunucuya bağlanılamadı! İnternet bağlantınızı kontrol edin.';
      }
      
      alert('Görsel yüklenirken hata oluştu: ' + errorMessage);
    }
    
    // Input'u temizle
    e.target.value = '';
  };


  return (
    <div className="standings-page">
      <h1 className="page-title">PUAN DURUMU</h1>

      {isAdmin && (
        <div className="admin-controls">
          <div className="admin-buttons-row">
            <button 
              className="add-player-button" 
              onClick={() => {
                setShowAddPlayerForm(!showAddPlayerForm);
              }}
            >
              {showAddPlayerForm ? 'İptal' : '+ Oyuncu Ekle'}
            </button>
          </div>
        </div>
      )}

      {isAdmin && showAddPlayerForm && (
        <div className="add-player-form">
          <h3>Yeni Oyuncu Ekle</h3>
          <div className="form-group">
            <label>
              Oyuncu Adı: *
              <input
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                placeholder="Örn: Ahmet Yılmaz"
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Pozisyon:
              <input
                type="text"
                value={newPlayerPosition}
                onChange={(e) => setNewPlayerPosition(e.target.value)}
                placeholder="Örn: Forvet, Orta Saha"
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Oyuncu Kodu: *
              <input
                type="text"
                value={newPlayerCode}
                onChange={(e) => setNewPlayerCode(e.target.value)}
                placeholder="Örn: #ahmet"
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Oyuncu Görseli (Kadro):
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </label>
            {newPlayerImage && (
              <img src={ensureHttps(newPlayerImage)} alt="Önizleme" className="image-preview" />
            )}
          </div>
          <button className="submit-button" onClick={handleAddPlayer}>
            Oyuncuyu Ekle
          </button>
        </div>
      )}

      <div className="season-selector-top">
        <label>
          Sezon:
          <select 
            value={selectedSeason} 
            onChange={(e) => {
              const newSeason = e.target.value;
              console.log('StandingsPage: onChange called with:', newSeason);
              setSelectedSeason(newSeason);
            }}
          >
            <option value="all">Tüm Zamanlar</option>
            {seasons.map(season => (
              <option key={season.id} value={season.id}>{season.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="season-selector-top">
        <label>
          Sırala:
          <select 
            value={selectedSort} 
            onChange={(e) => setSelectedSort(e.target.value)}
          >
            <option value="default">PUAN DURUMU</option>
            <option value="son5">SON 5 MAÇ</option>
            <option value="mac">MAÇ SAYISI</option>
            <option value="galibiyet">GALİBİYET</option>
            <option value="beraberlik">BERABERLİK</option>
            <option value="maglubiyet">MAĞLUBİYET</option>
            <option value="golKatkisi">GOL KATKISI</option>
            <option value="gol">GOL</option>
            <option value="asist">ASİST</option>
            <option value="kart">KART</option>
            <option value="siyahKart">SİYAH KART</option>
            <option value="mvdpKart">MVDP KART</option>
          </select>
        </label>
      </div>

      <div className="players-list">
        {players.length === 0 ? (
          <div className="no-players">Henüz oyuncu eklenmemiş.</div>
        ) : (
          (() => {
            // Sıralama filtresine göre oyuncuları sırala
            const sortedPlayers = selectedSort === 'mac'
              ? [...players].sort((a, b) => {
                  const aMatches = (playersStats[a.id]?.matches || 0);
                  const bMatches = (playersStats[b.id]?.matches || 0);
                  return bMatches - aMatches;
                })
              : selectedSort === 'gol'
              ? [...players].sort((a, b) => {
                  const aGoals = (playersStats[a.id]?.goals || 0);
                  const bGoals = (playersStats[b.id]?.goals || 0);
                  return bGoals - aGoals;
                })
              : selectedSort === 'golKatkisi'
              ? [...players].sort((a, b) => {
                  const aContribution = (playersStats[a.id]?.goals || 0) + (playersStats[a.id]?.assists || 0);
                  const bContribution = (playersStats[b.id]?.goals || 0) + (playersStats[b.id]?.assists || 0);
                  return bContribution - aContribution;
                })
              : selectedSort === 'galibiyet'
              ? [...players].sort((a, b) => {
                  const aWins = (playersStats[a.id]?.wins || 0);
                  const bWins = (playersStats[b.id]?.wins || 0);
                  return bWins - aWins;
                })
              : selectedSort === 'beraberlik'
              ? [...players].sort((a, b) => {
                  const aDraws = (playersStats[a.id]?.draws || 0);
                  const bDraws = (playersStats[b.id]?.draws || 0);
                  return bDraws - aDraws;
                })
              : selectedSort === 'maglubiyet'
              ? [...players].sort((a, b) => {
                  const aLosses = (playersStats[a.id]?.losses || 0);
                  const bLosses = (playersStats[b.id]?.losses || 0);
                  return bLosses - aLosses;
                })
              : selectedSort === 'asist'
              ? [...players].sort((a, b) => {
                  const aAssists = (playersStats[a.id]?.assists || 0);
                  const bAssists = (playersStats[b.id]?.assists || 0);
                  return bAssists - aAssists;
                })
              : selectedSort === 'siyahKart'
              ? [...players].sort((a, b) => {
                  const aBlackCards = (playersStats[a.id]?.blackCards || 0);
                  const bBlackCards = (playersStats[b.id]?.blackCards || 0);
                  return bBlackCards - aBlackCards;
                })
              : selectedSort === 'mvdpKart'
              ? [...players].sort((a, b) => {
                  const aMvdpCards = (playersStats[a.id]?.mvdpCards || 0);
                  const bMvdpCards = (playersStats[b.id]?.mvdpCards || 0);
                  return bMvdpCards - aMvdpCards;
                })
              : selectedSort === 'kart'
              ? [...players].sort((a, b) => {
                  const aTotalCards = (playersStats[a.id]?.blackCards || 0) + (playersStats[a.id]?.mvdpCards || 0);
                  const bTotalCards = (playersStats[b.id]?.blackCards || 0) + (playersStats[b.id]?.mvdpCards || 0);
                  return bTotalCards - aTotalCards;
                })
              : selectedSort === 'son5'
              ? (() => {
                  const getLast5FormPoints = (playerId: string): number => {
                    const playerMatches = seasonMatches
                      .filter(m => m && m.blackTeam && m.whiteTeam && (m.blackTeam.includes(playerId) || m.whiteTeam.includes(playerId)))
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .slice(0, 5);
                    let points = 0;
                    playerMatches.forEach(match => {
                      const isBlack = match.blackTeam.includes(playerId);
                      const blackWon = match.blackScore > match.whiteScore;
                      const whiteWon = match.whiteScore > match.blackScore;
                      const draw = match.blackScore === match.whiteScore;
                      if (draw) points += 0;
                      else if ((isBlack && blackWon) || (!isBlack && whiteWon)) points += 1;
                      else points -= 1;
                    });
                    return points;
                  };
                  return [...players].sort((a, b) => getLast5FormPoints(b.id) - getLast5FormPoints(a.id));
                })()
              : players;

            return sortedPlayers.map((player, index) => {
            const stats = playersStats[player.id] || {
              matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0,
              ownGoals: 0, points: 0, blackCards: 0, mvdpCards: 0,
            };
            const rankChange = rankChanges[player.id] || null;
            const totalPlayers = sortedPlayers.length;
            const isTopThree = index < 3;
            const isBottomThree = index >= totalPlayers - 3;
            
            return (
              <div 
                key={player.id} 
                className={`player-card ${isTopThree ? 'top-three' : ''} ${isBottomThree ? 'bottom-three' : ''}`}
                onClick={() => setSelectedPlayer(player)}
              >
                <div className="player-rank">{index + 1}</div>
                {rankChange === 'up' && (
                  <span className="trend-indicator trend-up">↑</span>
                )}
                {rankChange === 'down' && (
                  <span className="trend-indicator trend-down">↓</span>
                )}
                {(rankChange === 'same' || rankChange === null) && (
                  <span className="trend-indicator trend-same">-</span>
                )}
                <div className="player-image">
                  {player.profileImage ? (
                    <img 
                      src={ensureHttps(player.profileImage)} 
                      alt={player.name}
                      onError={(e) => {
                        // Görsel yüklenemezse alternatif göster
                        console.error('[StandingsPage] Profil görseli yüklenemedi:', player.profileImage);
                        const target = e.target as HTMLImageElement;
                        const parent = target.parentElement;
                        if (!parent) return;
                        
                        // Önce mevcut placeholder'ı kontrol et
                        const existingPlaceholder = parent.querySelector('.player-placeholder');
                        if (existingPlaceholder) return;
                        
                        target.style.display = 'none';
                        
                        // Base64 string ise (eski format) uyarı göster
                        if (player.profileImage && player.profileImage.startsWith('data:image')) {
                          console.warn('[StandingsPage] Eski base64 format görsel tespit edildi:', player.name);
                          // Placeholder göster
                          const placeholder = document.createElement('div');
                          placeholder.className = 'player-placeholder';
                          placeholder.textContent = player.name[0];
                          placeholder.title = 'Eski format görsel. Lütfen yönetici modundan yeni görsel yükleyin.';
                          parent.appendChild(placeholder);
                          return;
                        }
                        
                        // Alternatif görsel varsa onu dene
                        if (player.image && player.image !== player.profileImage) {
                          const altImg = document.createElement('img');
                          altImg.src = ensureHttps(player.image);
                          altImg.alt = player.name;
                          altImg.className = target.className;
                          altImg.onerror = () => {
                            altImg.style.display = 'none';
                            const placeholder = document.createElement('div');
                            placeholder.className = 'player-placeholder';
                            placeholder.textContent = player.name[0];
                            parent.appendChild(placeholder);
                          };
                          parent.appendChild(altImg);
                        } else {
                          // Placeholder göster
                          const placeholder = document.createElement('div');
                          placeholder.className = 'player-placeholder';
                          placeholder.textContent = player.name[0];
                          parent.appendChild(placeholder);
                        }
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : player.image ? (
                    <img 
                      src={ensureHttps(player.image)} 
                      alt={player.name}
                      onError={(e) => {
                        // Görsel yüklenemezse placeholder göster
                        console.error('[StandingsPage] Oyuncu görseli yüklenemedi:', player.image);
                        const target = e.target as HTMLImageElement;
                        const parent = target.parentElement;
                        if (!parent) return;
                        
                        target.style.display = 'none';
                        
                        const existingPlaceholder = parent.querySelector('.player-placeholder');
                        if (!existingPlaceholder) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'player-placeholder';
                          placeholder.textContent = player.name[0];
                          parent.appendChild(placeholder);
                        }
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="player-placeholder">{player.name[0]}</div>
                  )}
                </div>
                <div className="player-info">
                  <div className="player-name-container">
                    <div className="player-name">{toTurkishUpperCase(player.name)}</div>
                    {isAdmin && (
                      <div className="player-admin-actions" onClick={(e) => e.stopPropagation()}>
                        <label className="profile-image-upload-button" title="Profil Fotoğrafı Ekle">
                          <img src={`${process.env.PUBLIC_URL || ''}/icons/profile-icon.png`} alt="Profil Fotoğrafı" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleProfileImageUpload(player.id, e)}
                            style={{ display: 'none' }}
                          />
                        </label>
                        <button 
                          className="delete-player-button"
                          onClick={(e) => handleDeletePlayer(player.id, e)}
                          title="Oyuncuyu Sil"
                        >
                          <img src={`${process.env.PUBLIC_URL || ''}/icons/delete-icon.png`} alt="Sil" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="player-position">{player.position}</div>
                </div>
                <div className="player-stats">
                  {selectedSort === 'mac' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.matches}</span>
                      <span className="stat-label">MAÇ</span>
                    </div>
                  ) : selectedSort === 'gol' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.goals}</span>
                      <span className="stat-label">GOL</span>
                    </div>
                  ) : selectedSort === 'golKatkisi' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.goals + stats.assists}</span>
                      <span className="stat-label">GOL KATKISI</span>
                    </div>
                  ) : selectedSort === 'galibiyet' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.wins}</span>
                      <span className="stat-label">GALİBİYET</span>
                    </div>
                  ) : selectedSort === 'beraberlik' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.draws}</span>
                      <span className="stat-label">BERABERLİK</span>
                    </div>
                  ) : selectedSort === 'maglubiyet' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.losses}</span>
                      <span className="stat-label">MAĞLUBİYET</span>
                    </div>
                  ) : selectedSort === 'asist' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.assists}</span>
                      <span className="stat-label">ASİST</span>
                    </div>
                  ) : selectedSort === 'siyahKart' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.blackCards}</span>
                      <span className="stat-label">SİYAH KART</span>
                    </div>
                  ) : selectedSort === 'mvdpKart' ? (
                    <div className="stat-item stat-item-primary stat-item-single">
                      <span className="stat-value">{stats.mvdpCards}</span>
                      <span className="stat-label">MVDP KART</span>
                    </div>
                  ) : selectedSort === 'kart' ? (
                    <div className="stat-item stat-item-primary stat-item-single stat-item-kart">
                      <span className="stat-value">{stats.blackCards + stats.mvdpCards}</span>
                      <span className="stat-label">SİYAH:{stats.blackCards} MVDP:{stats.mvdpCards}</span>
                    </div>
                  ) : selectedSort === 'son5' ? (
                    <div className="stat-item stat-item-son5">
                      {(() => {
                        const playerMatches = seasonMatches
                          .filter(m => m && m.blackTeam && m.whiteTeam && (m.blackTeam.includes(player.id) || m.whiteTeam.includes(player.id)))
                          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                          .slice(0, 5)
                          .reverse();
                        const results: Array<'win' | 'draw' | 'loss'> = [];
                        playerMatches.forEach(match => {
                          const isBlack = match.blackTeam.includes(player.id);
                          const blackWon = match.blackScore > match.whiteScore;
                          const whiteWon = match.whiteScore > match.blackScore;
                          const draw = match.blackScore === match.whiteScore;
                          if (draw) results.push('draw');
                          else if ((isBlack && blackWon) || (!isBlack && whiteWon)) results.push('win');
                          else results.push('loss');
                        });
                        const renderSon5Icon = (result: 'win' | 'draw' | 'loss' | undefined, index: number) => {
                          const cls = result === 'win' ? 'son5-win' : result === 'draw' ? 'son5-draw' : result === 'loss' ? 'son5-loss' : 'son5-empty';
                          return (
                            <span key={index} className={`son5-icon ${cls}`}>
                              {result === 'win' ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              ) : result === 'draw' ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <circle cx="12" cy="12" r="9" />
                                </svg>
                              ) : result === 'loss' ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                  <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                              )}
                            </span>
                          );
                        };
                        return (
                          <div className="son5-icons">
                            {[0, 1, 2, 3, 4].map(i => renderSon5Icon(results[i], i))}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <>
                      <div className="stat-item stat-item-primary">
                        <span className="stat-label">Puan</span>
                        <span className="stat-value">{stats.points}</span>
                      </div>
                      <div className="stat-item stat-item-secondary">
                        <span className="stat-label">Maç</span>
                        <span className="stat-value">{stats.matches}</span>
                      </div>
                      <div className="stat-item stat-item-secondary">
                        <span className="stat-label">G</span>
                        <span className="stat-value">{stats.wins}</span>
                      </div>
                      <div className="stat-item stat-item-secondary">
                        <span className="stat-label">B</span>
                        <span className="stat-value">{stats.draws}</span>
                      </div>
                      <div className="stat-item stat-item-secondary">
                        <span className="stat-label">M</span>
                        <span className="stat-value">{stats.losses}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          });
          })()
        )}
      </div>

      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          isCompact={false}
        />
      )}
    </div>
  );
};

export default StandingsPage;


